import { MutableRefObject, JSX, useRef, useEffect, useCallback } from 'react';
import { Modal } from '@bg-components/Modal';
import { useLobbyTabsHook } from '@iframe-shell/routes/Lobby/hooks/useLobbyTabsHook';
import { LobbyTabs } from '@iframe-shell/routes/Lobby/LobbyTabs';
import { LobbyContent } from '@iframe-shell/routes/Lobby/LobbyContent';
import { useClickOutside } from '@betgames/bg-tools';
import { IModalProps, ModalName, analyticsApi, iframeIntegration, sessionStore } from '@bg-shared';
import classes from './LobbyModal.module.scss';

interface IProps {
    lobbyButtonRef: MutableRefObject<HTMLButtonElement>;
    setOpenDropdown(value: boolean): void;
}

export const LobbyModal = (props: IProps & IModalProps): JSX.Element => {
    const { activeTabId, setActiveTabId, getGamesByTab, availableTabs, hwbGames } =
        useLobbyTabsHook();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        iframeIntegration.lobbyOpened();
    }, []);

    useClickOutside((target: Node) => {
        if (ref.current?.contains(target) || props.lobbyButtonRef.current?.contains(target)) {
            return;
        }
        props.setOpenDropdown(false);
        props.onClose();
    });

    const handleGameClick = useCallback(() => {
        analyticsApi.trackOpenGameFromLobby();
        props.onClose();
    }, [props.onClose]);

    return (
        <Modal
            modalName={ModalName.LOBBY}
            className={classes.modal}
            backdropClassName={classes.backdrop}
            animation={{
                fadeInClassName: classes.slideIn,
                fadeOutClassName: classes.slideOut,
                duration: Number(classes.animationDuration),
            }}
        >
            <div className={classes.wrapper} ref={ref}>
                {sessionStore.lobbyLiveGames ? (
                    <LobbyContent hwbGames={hwbGames} />
                ) : (
                    <>
                        <LobbyTabs
                            tabs={availableTabs}
                            activeTabId={activeTabId}
                            onTabChange={setActiveTabId}
                            allTabSelected={activeTabId === availableTabs[0].id}
                        />

                        <LobbyContent
                            activeTabId={activeTabId}
                            availableTabs={availableTabs}
                            getGamesByTab={getGamesByTab}
                            onCardClick={handleGameClick}
                            navigationClassName={classes.modalNavigation}
                        />
                    </>
                )}
            </div>
        </Modal>
    );
};
